import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Shield, Eye, EyeOff, Building2, User } from 'lucide-react';
import { formatCPF, looksLikeCPF, formatCNPJ, looksLikeCNPJ, cleanCNPJ } from '../lib/documentUtils';

export default function Login() {
  const navigate = useNavigate();
  const { login, loginWithCNPJ } = useAuth();
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginMode, setLoginMode] = useState('personal'); // 'personal' or 'company'
  const [form, setForm] = useState({
    identifier: '',
    password: ''
  });
  
  // Company login form
  const [companyForm, setCompanyForm] = useState({
    cnpj: '',
    userIdentifier: '', // CPF ou email do usuário
    password: ''
  });
  
  // Show/hide password state
  const [showPassword, setShowPassword] = useState(false);
  
  // Multiple profiles state
  const [showProfileSelect, setShowProfileSelect] = useState(false);
  const [availableProfiles, setAvailableProfiles] = useState([]);

  // Load saved credentials on mount
  useEffect(() => {
    const savedCredentials = localStorage.getItem('g2_saved_login');
    if (savedCredentials) {
      try {
        const { identifier, password } = JSON.parse(savedCredentials);
        setForm({ identifier: identifier || '', password: password || '' });
        setRememberMe(true);
      } catch (e) {
        localStorage.removeItem('g2_saved_login');
      }
    }
  }, []);

  // Save or remove credentials when rememberMe changes or on successful login
  const saveCredentials = () => {
    if (rememberMe && form.identifier && form.password) {
      localStorage.setItem('g2_saved_login', JSON.stringify({
        identifier: form.identifier,
        password: form.password
      }));
    } else if (!rememberMe) {
      localStorage.removeItem('g2_saved_login');
    }
  };

  const handleIdentifierChange = (e) => {
    let value = e.target.value;
    
    // Auto-format if it looks like a CPF
    if (looksLikeCPF(value)) {
      value = formatCPF(value);
    }
    
    setForm({ ...form, identifier: value });
    // Reset profile selection when identifier changes
    setShowProfileSelect(false);
    setAvailableProfiles([]);
  };
  
  // Handle CNPJ input with new alphanumeric format
  const handleCNPJChange = (e) => {
    let value = e.target.value;
    
    // Auto-format CNPJ (alfanumérico)
    if (looksLikeCNPJ(value) || value.length > 0) {
      value = formatCNPJ(value);
    }
    
    setCompanyForm({ ...companyForm, cnpj: value });
  };
  
  // Handle company user identifier (CPF or email)
  const handleCompanyUserChange = (e) => {
    let value = e.target.value;
    
    if (looksLikeCPF(value)) {
      value = formatCPF(value);
    }
    
    setCompanyForm({ ...companyForm, userIdentifier: value });
  };
  
  const handleProfileSelect = async (role) => {
    setLoading(true);
    try {
      const user = await login(form.identifier, form.password, role);
      handleLoginSuccess(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLoginSuccess = (user) => {
    // Save credentials if "Remember Me" is checked
    saveCredentials();
    
    // Check if user needs to change password (forced by admin)
    if (user.force_password_change) {
      toast.warning('Você precisa criar uma nova senha para continuar', { duration: 5000 });
      navigate('/change-password-required');
      return;
    }
    
    // Check if user needs to update CPF
    if (user.needs_cpf_update) {
      toast.warning('Por favor, atualize seu cadastro com seu CPF', { duration: 5000 });
    } else {
      toast.success('Login realizado com sucesso!');
    }
    
    // Redirect based on role
    if (user.role === 'ADMIN') {
      navigate('/admin');
    } else if (user.role === 'COMPANY') {
      navigate('/company');
    } else if (user.role === 'PARTNER') {
      navigate('/partner');
    } else if (user.role === 'CANDIDATE') {
      // Candidatos devem acessar apenas pelo app mobile
      toast.info('Redirecionando para o aplicativo...', { duration: 2000 });
      navigate('/m');
    } else {
      navigate('/m');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.identifier, form.password);
      handleLoginSuccess(user);
    } catch (error) {
      // Check if it's a multiple profiles error
      if (error.message === 'MULTIPLE_PROFILES' && error.profiles) {
        setAvailableProfiles(error.profiles);
        setShowProfileSelect(true);
        toast.info('Selecione qual perfil deseja acessar');
      } else {
        // Ensure error message is always a string
        let errorMessage = 'Erro ao fazer login';
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          if (typeof detail === 'string') {
            errorMessage = detail;
          } else if (Array.isArray(detail)) {
            // Pydantic validation errors come as array
            errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
          } else if (typeof detail === 'object') {
            errorMessage = detail.msg || detail.message || JSON.stringify(detail);
          }
        }
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Handle company login (CNPJ + user)
  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    
    const cnpjClean = cleanCNPJ(companyForm.cnpj);
    if (cnpjClean.length !== 14) {
      toast.error('CNPJ inválido. Deve ter 14 caracteres.');
      return;
    }
    
    setLoading(true);
    try {
      const user = await loginWithCNPJ(cnpjClean, companyForm.userIdentifier, companyForm.password);
      handleLoginSuccess(user);
    } catch (error) {
      let errorMessage = 'Erro ao fazer login';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      'ADMIN': 'Administrador',
      'COMPANY': 'Empresa (Recrutador)',
      'CANDIDATE': 'Candidato',
      'PARTNER': 'Parceiro'
    };
    return labels[role] || role;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1761229170427-487959bfb653?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBhYnN0cmFjdCUyMGJsdWV8ZW58MHx8fHwxNzcxOTAwMjQxfDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 flex flex-col justify-center p-12">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">G2</span>
            </div>
            <span className="font-bold text-2xl text-white">G2eGente</span>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">
            Bem-vindo de volta
          </h2>
          <p className="text-lg text-slate-300">
            Acesse sua conta e continue conectando talentos de forma ética.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <Button 
            variant="ghost" 
            className="mb-8"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>

          <Card className="border-slate-200 shadow-xl">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-slate-900">
                Entrar
              </CardTitle>
              <CardDescription>
                Digite suas credenciais para acessar sua conta
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Aviso para Candidatos */}
              <div className="mb-4 p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
                <p className="text-sm text-cyan-800">
                  <strong>Candidatos:</strong> Use o <a href="/m/login" className="text-cyan-600 underline font-semibold">aplicativo mobile</a> para acessar suas vagas e candidaturas.
                </p>
              </div>
              
              {/* Profile Selection UI */}
              {showProfileSelect && availableProfiles.length > 0 ? (
                <div className="space-y-4">
                  <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                    <p className="text-sm text-cyan-800 font-medium mb-3">
                      Este CPF está cadastrado em múltiplos perfis. Selecione qual deseja acessar:
                    </p>
                    <div className="space-y-2">
                      {availableProfiles.map((profile, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          className="w-full justify-start h-auto py-3 px-4"
                          onClick={() => handleProfileSelect(profile.role)}
                          disabled={loading}
                        >
                          <div className="text-left">
                            <div className="font-semibold">{getRoleLabel(profile.role)}</div>
                            <div className="text-xs text-slate-500">{profile.email}</div>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    className="w-full"
                    onClick={() => {
                      setShowProfileSelect(false);
                      setAvailableProfiles([]);
                    }}
                  >
                    Voltar
                  </Button>
                </div>
              ) : (
                <>
              {/* Login Mode Tabs */}
              <Tabs value={loginMode} onValueChange={setLoginMode} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="personal" className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Pessoal
                  </TabsTrigger>
                  <TabsTrigger value="company" className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Empresa
                  </TabsTrigger>
                </TabsList>
                
                {/* Personal Login Tab */}
                <TabsContent value="personal">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="identifier">CPF ou Email</Label>
                  <Input
                    id="identifier"
                    type="text"
                    placeholder="000.000.000-00 ou seu@email.com"
                    value={form.identifier}
                    onChange={handleIdentifierChange}
                    required
                    data-testid="login-identifier-input"
                  />
                  <p className="text-xs text-slate-500">
                    Usuários antigos podem usar o email cadastrado
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      required
                      data-testid="login-password-input"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                {/* Remember Me Checkbox */}
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="rememberMe" 
                    checked={rememberMe}
                    onCheckedChange={(checked) => {
                      setRememberMe(checked);
                      if (!checked) {
                        localStorage.removeItem('g2_saved_login');
                      }
                    }}
                  />
                  <label 
                    htmlFor="rememberMe" 
                    className="text-sm text-slate-600 cursor-pointer select-none"
                  >
                    Lembrar meus dados de acesso
                  </label>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-slate-900 hover:bg-slate-800"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    'Entrar'
                  )}
                </Button>
              </form>
                </TabsContent>
                
                {/* Company Login Tab */}
                <TabsContent value="company">
                  <form onSubmit={handleCompanySubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="cnpj">CNPJ da Empresa</Label>
                      <Input
                        id="cnpj"
                        type="text"
                        placeholder="AB.C12.345/6001-99"
                        value={companyForm.cnpj}
                        onChange={handleCNPJChange}
                        required
                        data-testid="login-cnpj-input"
                      />
                      <p className="text-xs text-slate-500">
                        Novo formato alfanumérico: AA.AAA.AAA/AAAA-00
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="companyUser">CPF ou Email do Usuário</Label>
                      <Input
                        id="companyUser"
                        type="text"
                        placeholder="000.000.000-00 ou seu@email.com"
                        value={companyForm.userIdentifier}
                        onChange={handleCompanyUserChange}
                        required
                        data-testid="login-company-user-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="companyPassword">Senha</Label>
                      <div className="relative">
                        <Input
                          id="companyPassword"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={companyForm.password}
                          onChange={(e) => setCompanyForm({ ...companyForm, password: e.target.value })}
                          required
                          data-testid="login-company-password-input"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-cyan-600 hover:bg-cyan-700"
                      disabled={loading}
                      data-testid="login-company-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Entrando...
                        </>
                      ) : (
                        <>
                          <Building2 className="w-4 h-4 mr-2" />
                          Entrar na Empresa
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
              
              <div className="mt-4 text-center">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-slate-500 hover:text-cyan-600"
                >
                  Esqueci minha senha
                </Link>
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-slate-600">
                  Não tem uma conta?{' '}
                  <Link 
                    to="/register" 
                    className="text-cyan-600 hover:text-cyan-700 font-medium"
                  >
                    Cadastre-se
                  </Link>
                </p>
              </div>
              
              {/* Admin Access Link */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <Link 
                  to="/admin/login"
                  className="flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                >
                  <Shield className="w-4 h-4" />
                  Acesso Administrativo
                </Link>
              </div>
              </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
